/**
 * Resumable video upload — the browser half of the service's `uploads.py`.
 *
 *   POST /v1/videos {filename, size, sha256}              → {video_id, upload_key}
 *   GET  /v1/uploads/{key}?upload_id=<sha256>             → {offset}   where to resume from
 *   PUT  /v1/artifacts/{key}  Content-Range + X-Upload-Id → {offset, complete}
 *        409 {error: "range_mismatch", offset}            the server's truth about where we are
 *
 * The rules are pure functions (`nextRange`, `offsetAfter`) so the two ways an upload goes wrong — a
 * dropped connection and a chunk the server did not see acknowledged — are covered by tests rather than
 * by trying it on a slow link. The server flips the video to `ready` by itself when the final byte lands
 * and the digest matches; a mismatch comes back as `video.status === 'error'` with the reason.
 */

import { ApiError, type TikTokHubApi } from './client'
import type { Source, SourceKind, Video } from './types'

export const CHUNK_BYTES = 8 * 1024 * 1024

export interface Range {
  start: number
  end: number // exclusive
}

/** The next range to send from `offset`, or null when the file is fully sent. */
export function nextRange(offset: number, total: number, chunk = CHUNK_BYTES): Range | null {
  if (offset >= total) return null
  return { start: offset, end: Math.min(offset + chunk, total) }
}

/**
 * Where the upload stands after the server answered a range.
 *
 * A 409 is not a failure to retry from the same place: the server says which offset it holds, and that
 * is the truth (a chunk may have landed whose acknowledgement never reached us). Anything else is a real
 * error and is rethrown.
 */
export function offsetAfter(
  outcome: { ok: true; offset: number } | { ok: false; status: number; offset?: number | null },
): number {
  if (outcome.ok) return outcome.offset
  if (outcome.status === 409 && typeof outcome.offset === 'number') return outcome.offset
  throw new Error(`upload refused: HTTP ${outcome.status}`)
}

export interface UploadProgress {
  stage: 'hashing' | 'uploading' | 'done'
  done: number
  total: number
}

/** SHA-256 of the whole file, as the service verifies it. Needs the file in memory once. */
export async function sha256Hex(file: Blob): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer())
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

/** What `uploadFile` needs from an announce call: the key to write to and the record as announced. */
interface Announced<T> {
  upload_key: string
  record: T
}

async function uploadFile<T extends { status: string }>(
  api: TikTokHubApi,
  file: File,
  announce: (sha256: string) => Promise<Announced<T>>,
  reread: () => Promise<T>,
  onProgress: (p: UploadProgress) => void,
  signal?: AbortSignal,
): Promise<T> {
  onProgress({ stage: 'hashing', done: 0, total: file.size })
  const sha256 = await sha256Hex(file)
  const created = await announce(sha256)
  const key = created.upload_key
  let offset = (await api.uploadOffset(key, sha256)).offset ?? 0
  let last: T = created.record

  for (;;) {
    const range = nextRange(offset, file.size)
    if (!range) break
    if (signal?.aborted) throw new Error('upload cancelled')
    try {
      const accepted = await api.putRange(key, file.slice(range.start, range.end), range.start, file.size, sha256, signal)
      offset = offsetAfter({ ok: true, offset: accepted.offset })
      const landed = (accepted as { video?: T; source?: T }).video ?? (accepted as { source?: T }).source
      if (landed) last = landed
    } catch (e) {
      if (e instanceof ApiError) {
        const detail = (e.detail as { detail?: { offset?: number } } | null)?.detail
        offset = offsetAfter({ ok: false, status: e.status, offset: detail?.offset ?? null })
        continue
      }
      throw e
    }
    onProgress({ stage: 'uploading', done: offset, total: file.size })
  }
  onProgress({ stage: 'done', done: file.size, total: file.size })
  // The final range's answer carries the verdict; when it did not (an old server), ask.
  return last.status === 'uploading' ? reread() : last
}

export function uploadVideo(
  api: TikTokHubApi,
  file: File,
  directionId: string,
  onProgress: (p: UploadProgress) => void,
  signal?: AbortSignal,
): Promise<Video> {
  let videoId = ''
  return uploadFile<Video>(
    api,
    file,
    async (sha256) => {
      const created = await api.createVideo({ filename: file.name, size: file.size, sha256, direction_id: directionId })
      videoId = created.video_id
      return { upload_key: created.upload_key, record: created.video }
    },
    () => api.video(videoId),
    onProgress,
    signal,
  )
}

/** A uniquification source goes up exactly like a video, announced on `/v1/sources` with its kind. */
export function uploadSource(
  api: TikTokHubApi,
  kind: SourceKind,
  file: File,
  onProgress: (p: UploadProgress) => void,
  signal?: AbortSignal,
): Promise<Source> {
  let sourceId = ''
  return uploadFile<Source>(
    api,
    file,
    async (sha256) => {
      const created = await api.createSource({ kind, filename: file.name, size: file.size, sha256 })
      sourceId = created.source_id
      return { upload_key: created.upload_key, record: created.source }
    },
    async () => (await api.sources()).sources.find((s) => s.source_id === sourceId)!,
    onProgress,
    signal,
  )
}
